<template>
  <div
    class="section-title"
    :class="['is-' + align, 'is-' + tone, { 'is-flush': flush }]"
  >
    <p v-if="kicker" class="section-kicker">{{ kicker }}</p>
    <component :is="'h' + level" v-if="title" class="section-heading">
      <template v-for="(line, index) in titleLines" :key="index">
        <br v-if="index" />{{ line.lead }}<em v-if="line.accent">{{ line.accent }}</em>{{ line.tail }}
      </template>
    </component>
    <p v-if="lede" class="section-lede">{{ lede }}</p>
    <slot />
  </div>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* The one section title on the site. Every page and every shared component
   renders its title through this, so the chrome cannot drift between them.

   Two independent axes, which is all the variants that ever existed:
     align  centre (38rem, lifted 2.25rem) or left (40rem, lifted 1.75rem)
     tone   the ground the block sits on: light, dark or brand. The ground sets
            the kicker and the accent together, from the $label-on-* steps, so a
            page never names a colour.

   The kicker is one treatment, not two. It used to be a grey sentence-case
   line by default and a separate uppercase accent "eyebrow" behind a prop,
   and the two were always the same job: a small label naming the section that
   follows. The type spec is the shared $label-* step now, uppercase in Rubik,
   and the ground changes only its colour. The `eyebrow` prop went with it,
   because a prop that selects nothing is a second way to say one thing.

   `brand` is the blue band: the hero, the strength panel, the CTA and the
   report CTA. It is its own tone rather than a `dark` variant because two
   things change on it. The label goes white, because $dark-accent is a light
   blue and sat between 1.35:1 and 2.15:1 against the band. And the accent is
   dropped: the heading is already white there, and its accented word was the
   same light blue, so an entire word per heading was very nearly the colour of
   its own ground. White is the only step that holds on mid-blue, so the heading
   reads as one statement and carries no accent.

   The lede rides along as a prop rather than a separate class, because it had
   drifted into four near-identical copies (.brief-lede, .ma-intro, .bp-lede
   and the bare p inside .plan-title).

   One slot, after the lede. It is for anything that follows the sentence the
   section opens with: an action row, or a row of chips summarising what the
   sentence just said. The position is the component's, and the row's own step
   above the lede is `.action-row`'s, so two callers that put a row there get the
   same gap between the same lines.

   There used to be a `meta` slot as well, for a row printed between the heading
   and the lede. Its only caller was the case study's badge row, and when the
   badges moved under the sentence the slot was left with nothing to print: a
   position no caller uses is a second way of saying the thing the slot below it
   already says, so it went rather than staying as a shape the site does not use.

   The heading level is a prop and defaults to 2. It used to emit an h1
   unconditionally, which made every section head on the site a top-level
   heading: the home page carried five h1 and WebDesign carried three, so the
   page's real h1 was indistinguishable from its sections. The hero owns the
   h1 now and every section head is an h2, or deeper where a section nests.

   The kicker is a p, not an h5. It is a label, not a document heading, and
   putting it in the outline was the other half of the same problem.

   Headings are $text-heading on both grounds. The old split (centred titles
   inherited the global $text-strong, left titles were near-black) is gone: $text-strong
   survives only as $text-strong for table theads and inline emphasis. */
.section-title {
  .section-kicker {
    font-family: $headings-font-family;
    font-size: $label-size;
    font-weight: $label-weight;
    letter-spacing: $label-tracking;
    margin-bottom: 0.9rem;
    text-transform: uppercase;
  }

  /* Declared here rather than left to a bootstrap .mb-* utility on the
     element. That utility is how the centred titles drifted from a 1rem gap
     to a 1.5rem one with no diff in any stylesheet: the style block stayed
     silent and the markup class was the only truth. Owning the margin here
     is what makes it undriftable. */
  .section-heading {
    color: $text-heading;
    margin-bottom: 1rem;
  }

  em {
    font-style: normal;
  }

  &.is-center {
    margin: 0 auto 2.25rem;
    max-width: 38rem;
    text-align: center;
  }

  &.is-left {
    margin-bottom: 1.75rem;
    max-width: 40rem;
  }

  &.is-light {
    .section-kicker {
      color: $label-on-light;
    }

    em {
      color: $primary;
    }
  }

  &.is-dark {
    .section-kicker {
      color: $label-on-dark;
    }

    .section-heading {
      color: $on-dark-strong;
    }

    em {
      color: $dark-accent;
    }

    .section-lede {
      color: $on-dark-body;
    }
  }

  /* The blue band. Same heading and lede as `dark`; the label goes white and the
     heading's accent is dropped into the heading colour, because nothing else
     reaches 4.5:1 on mid-blue. */
  &.is-brand {
    .section-kicker {
      color: $label-on-brand;
    }

    .section-heading {
      color: $on-dark-strong;
    }

    em {
      color: $on-dark-strong;
    }

    .section-lede {
      color: $on-dark-body;
    }
  }
}

/* Light-section lede spec: 1rem / 1.5 / $text-body. The plan, FAQ and service
   block titles used to inherit 1rem / 1.5 from the body defaults and only name
   the spec in a comment; naming it here makes it one real declaration. Dark
   sections take the same size and rhythm at $on-dark-body, set above. */
.section-lede {
  color: $text-body;
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 0;
}

/* Zeroes the lift where the surrounding grid already spaces the column, which
   section 1 of the brand presence page needs to centre its copy against the
   kit sheet. A documented modifier, not a one-off style on the page.

   Declared as .section-title.is-flush rather than a bare .is-flush: the align
   modifiers above are two-class selectors, so a one-class rule lost that
   contest and this prop did nothing at all. It sits below them so the tie is
   settled by source order, and it must stay below them. */
.section-title.is-flush {
  margin-bottom: 0;
}
</style>

<script>
import { accentTitleLines } from '@/accentTitle';

export default {
  props: {
    kicker: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      required: true
    },
    lede: {
      type: String,
      default: ''
    },
    align: {
      type: String,
      default: 'center',
      validator: (value) => ['center', 'left'].includes(value)
    },
    tone: {
      type: String,
      default: 'light',
      validator: (value) => ['light', 'dark', 'brand'].includes(value)
    },
    /* The heading level this section head emits. Defaults to 2 because the
       page hero owns the h1. Pass 3 for a section nested inside another. */
    level: {
      type: Number,
      default: 2,
      validator: (value) => Number.isInteger(value) && value >= 1 && value <= 6
    },
    /* Cancels the bottom margin for the one layout that supplies its own. */
    flush: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    /* A title may carry a newline to force a line break, which keeps a
       two-clause heading off an awkward wrap. The last word of the last line
       takes the accent colour. A single-line title is the same call with one
       line, so every title on the site can take this path. */
    titleLines() {
      return accentTitleLines(this.title);
    }
  }
};
</script>
